// اللَّهُ لَا إِلَٰهَ إِلَّا هُوَ الْحَيُّ الْقَيُّومُ ۚ لَا تَأْخُذُهُ سِنَةٌ وَلَا نَوْمٌ ۚ
// لَهُ مَا فِي السَّمَاوَاتِ وَمَا فِي الْأَرْضِ ۗ مَنْ ذَا الَّذِي يَشْفَعُ عِنْدَهُ إِلَّا بِإِذْنِهِ ۚ
// يَعْلَمُ مَا بَيْنَ أَيْدِيهِمْ وَمَا خَلْفَهُمْ ۖ وَلَا يُحِيطُونَ بِشَيْءٍ مِنْ عِلْمِهِ إِلَّا بِمَا شَاءَ ۚ
// وَسِعَ كُرْسِيُّهُ السَّمَاوَاتِ وَالْأَرْضَ ۖ وَلَا يَئُودُهُ حِفْظُهُمَا ۚ وَهُوَ الْعَلِيُّ الْعَظِيمُ
// (Bakara 255)

import { Component, type ErrorInfo, type ReactNode } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { AlertTriangle, RefreshCw, LifeBuoy } from 'lucide-react';
import { sonHatayiKaydet } from '@/lib/sonHata';
import { useAktifUygulama } from '@/hooks/useAktifUygulama';

interface Props {
  readonly children: ReactNode;
  readonly resetKey?: string;
  // Uygulama belirlenebildiyse "ELPO'ya bildir" düğmesi çizilir; hata ve sayfa talebe kendiliğinden eklenir.
  readonly onReport?: () => void;
}

interface State {
  readonly error: Error | null;
  readonly stack: string;
  readonly resetKey: string;
}

// Üst seviye React hata yakalayıcı. Render sırasında patlayan tüm bileşenleri yakalar
// ve beyaz ekran yerine toparlanabilir bir hata UI'ı gösterir.
// resetKey (pathname) değişince state sıfırlanır, user başka sayfaya gidince sıkışmaz.
class ErrorBoundaryClass extends Component<Props, State> {
  constructor(props: Props) {
    super(props);
    this.state = { error: null, stack: '', resetKey: props.resetKey ?? '' };
  }

  static getDerivedStateFromError(error: Error): Partial<State> {
    return { error, stack: error.stack ?? '' };
  }

  static getDerivedStateFromProps(nextProps: Props, prevState: State): Partial<State> | null {
    if ((nextProps.resetKey ?? '') !== prevState.resetKey) {
      return { error: null, stack: '', resetKey: nextProps.resetKey ?? '' };
    }
    return null;
  }

  override componentDidCatch(error: Error, info: ErrorInfo): void {
    console.error('[ErrorBoundary]', error, info.componentStack);
    sonHatayiKaydet(error.message || 'Bilinmeyen hata');
  }

  private readonly reset = (): void => {
    this.setState({ error: null, stack: '' });
  };

  override render(): ReactNode {
    if (this.state.error) {
      return (
        <div className="min-h-screen flex items-center justify-center bg-background p-6">
          <div className="max-w-lg w-full bg-card border border-destructive/30 rounded-lg shadow-sm p-6">
            <div className="flex items-start gap-3">
              <AlertTriangle className="w-6 h-6 text-destructive flex-shrink-0 mt-0.5" />
              <div className="flex-1 min-w-0">
                <h2 className="text-[16px] font-semibold text-foreground">Bir hata oluştu</h2>
                <p className="text-[14px] text-muted-foreground mt-1 break-words">
                  {this.state.error.message || 'Bilinmeyen hata'}
                </p>
              </div>
            </div>

            <details className="mt-4 text-[12px] text-muted-foreground">
              <summary className="cursor-pointer select-none hover:text-foreground">
                Teknik ayrıntı
              </summary>
              <pre className="mt-2 p-2 bg-muted/30 rounded max-h-64 overflow-auto whitespace-pre-wrap break-words">
                {this.state.stack}
              </pre>
            </details>

            <div className="mt-5 flex items-center gap-2 justify-end">
              {this.props.onReport && (
                <button
                  type="button"
                  onClick={this.props.onReport}
                  className="mr-auto inline-flex items-center gap-1.5 px-3 py-1.5 text-[13px] border border-border rounded-md text-foreground hover:bg-accent transition-colors"
                >
                  <LifeBuoy className="w-3.5 h-3.5 text-elpo-turkuaz" />
                  ELPO'ya bildir
                </button>
              )}
              <button
                type="button"
                onClick={() => (window.location.href = '/')}
                className="px-3 py-1.5 text-[13px] border border-border rounded-md hover:bg-accent transition-colors"
              >
                Ana sayfa
              </button>
              <button
                type="button"
                onClick={this.reset}
                className="inline-flex items-center gap-1.5 px-3 py-1.5 text-[13px] bg-primary-600 text-white rounded-md hover:bg-primary-700 transition-colors btn-press"
              >
                <RefreshCw className="w-3.5 h-3.5" />
                Tekrar dene
              </button>
            </div>
          </div>
        </div>
      );
    }
    return this.props.children;
  }
}

export function ErrorBoundary({ children }: { readonly children: ReactNode }) {
  const { pathname } = useLocation();
  const navigate = useNavigate();
  const app = useAktifUygulama();
  const onReport = app ? () => navigate(`/${app}/support/report`, { state: { from: pathname } }) : undefined;
  return <ErrorBoundaryClass resetKey={pathname} onReport={onReport}>{children}</ErrorBoundaryClass>;
}
